import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Send, CreditCard, CheckCircle2, FileDown } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { pdfUrl } from "@/lib/files";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { money, formatDate } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const VAT_LABEL = { standard: "Con IVA", exempt: "Esente IVA", reverse_charge: "Reverse charge" };

export default function InvoiceDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [paying, setPaying] = useState(false);
  const [vatMode, setVatMode] = useState("standard");
  const [vatPct, setVatPct] = useState(0);
  const isStaff = ["admin", "support"].includes(user?.role);

  const load = async () => {
    try {
      const { data } = await api.get(`/invoices/${id}`);
      setInv(data); setVatMode(data.vat_mode || "standard"); setVatPct(data.vat_pct ?? 0);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [id]);

  const saveVat = async () => {
    try {
      const { data } = await api.patch(`/invoices/${id}/vat`, {
        vat_mode: vatMode, vat_pct: vatMode === "standard" ? Number(vatPct) : 0,
      });
      setInv(data); toast.success("Modalità IVA aggiornata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendInvoice = async () => {
    try { const { data } = await api.patch(`/invoices/${id}/send`); setInv(data); toast.success("Fattura inviata"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/checkout", { invoice_id: id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setPaying(false); }
  };

  if (!inv) return <div className="text-slate-400 py-10">{t("common.loading")}</div>;
  const paid = inv.status === "paid";
  const isOutgoing = inv.direction === "outgoing" || inv.source === "partner_invoice";

  return (
    <div className="space-y-6 max-w-3xl" data-testid="invoice-detail-page">
      <button onClick={() => navigate("/invoices")} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft size={16} /> {t("common.back")}</button>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-6 lg:p-8 border-b border-slate-100 flex items-start justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900">Fattura {inv.number}</h1>
            <p className="text-sm text-slate-500 mt-1">Emessa {formatDate(inv.issued_at)} · Scadenza {formatDate(inv.due_at)}</p>
          </div>
          <StatusBadge status={inv.status} />
        </div>

        <div className="p-6 lg:p-8 space-y-1">
          <div className="text-xs uppercase tracking-wider text-slate-400">Cliente</div>
          <div className="text-slate-800 font-medium">{inv.client_name || "—"}</div>
          {inv.client_email && <div className="text-sm text-slate-500">{inv.client_email}</div>}
        </div>

        <div className="px-6 lg:px-8 pb-6">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
              <th className="py-2">Descrizione</th><th className="py-2 text-right">Qtà</th><th className="py-2 text-right">Prezzo</th><th className="py-2 text-right">Totale</th>
            </tr></thead>
            <tbody>
              {(inv.lines || []).map((l, i) => (
                <tr key={i} className="border-b border-slate-50">
                  <td className="py-3 text-slate-700">{l.description}</td>
                  <td className="py-3 text-right">{l.quantity}</td>
                  <td className="py-3 text-right">{money(l.unit_price, inv.currency)}</td>
                  <td className="py-3 text-right font-medium">{money(l.quantity * l.unit_price, inv.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-4 ml-auto w-full max-w-xs space-y-1.5 text-sm">
            <div className="flex justify-between text-slate-500"><span>Imponibile</span><span>{money(inv.subtotal, inv.currency)}</span></div>
            <div className="flex justify-between text-slate-500"><span>IVA{inv.vat_mode === "standard" ? ` (${inv.vat_pct}%)` : ""}</span><span>{money(inv.tax, inv.currency)}</span></div>
            {inv.vat_note && <div className="text-xs text-slate-400 italic" data-testid="invoice-vat-note">{inv.vat_note}</div>}
            <div className="flex justify-between font-display text-lg font-bold text-slate-900 pt-2 border-t border-slate-200"><span>{t("common.total")}</span><span>{money(inv.total, inv.currency)}</span></div>
          </div>
        </div>

        {isStaff && (
          <div className="px-6 lg:px-8 pb-6 pt-2 border-t border-slate-100" data-testid="invoice-vat-control">
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Modalità IVA</div>
            <div className="flex flex-wrap items-end gap-3">
              <div className="w-48">
                <Select value={vatMode} onValueChange={setVatMode}>
                  <SelectTrigger data-testid="invoice-vat-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">{VAT_LABEL.standard}</SelectItem>
                    <SelectItem value="exempt">{VAT_LABEL.exempt}</SelectItem>
                    <SelectItem value="reverse_charge">{VAT_LABEL.reverse_charge}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {vatMode === "standard" && (
                <div className="w-28">
                  <Input type="number" value={vatPct} onChange={(e) => setVatPct(e.target.value)} placeholder="IVA %" data-testid="invoice-vat-pct" />
                </div>
              )}
              <Button variant="outline" size="sm" onClick={saveVat} data-testid="invoice-vat-save">Applica IVA</Button>
            </div>
          </div>
        )}

        <div className="p-6 lg:p-8 border-t border-slate-100 flex items-center justify-end gap-3 bg-slate-50">
          <a href={pdfUrl(`invoices/${id}/pdf`)} target="_blank" rel="noreferrer"><Button variant="outline" data-testid="invoice-pdf-btn"><FileDown size={16} className="mr-1.5" /> PDF</Button></a>
          {isStaff && !paid && <Button variant="outline" onClick={sendInvoice} data-testid="invoice-send-btn"><Send size={16} className="mr-1.5" /> {t("common.send")}</Button>}
          {isOutgoing ? (
            <span className="text-sm text-slate-500" data-testid="invoice-outgoing-note">Fattura partner (in uscita)</span>
          ) : paid ? (
            <span className="flex items-center gap-2 text-emerald-600 font-medium" data-testid="invoice-paid-badge"><CheckCircle2 size={18} /> {t("invoice.paid")}</span>
          ) : (
            <Button onClick={pay} disabled={paying} className="bg-blue-900 hover:bg-blue-800" data-testid="invoice-pay-btn">
              <CreditCard size={16} className="mr-1.5" /> {paying ? "..." : `${t("invoice.pay")} · ${money(inv.total, inv.currency)}`}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
